import { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2, Link2, Loader2, Network } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { useClientLevel } from '../../hooks/useClientLevel';
import AddEditSwitchGroupModal from './AddEditSwitchGroupModal';
import AssignSwitchModal from './AssignSwitchModal';

interface SwitchGroup {
    id: number;
    organization_id: number;
    client_id: number;
    name: string;
    description: string;
    created?: string;
    updated?: string;
}

interface SwitchGroupModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function SwitchGroupModal({ isOpen, onClose }: SwitchGroupModalProps) {
    const [groups, setGroups] = useState<SwitchGroup[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Modal Control States
    const [addEditOpen, setAddEditOpen] = useState(false);
    const [addEditMode, setAddEditMode] = useState<'add' | 'edit'>('add');
    const [selectedGroup, setSelectedGroup] = useState<SwitchGroup | null>(null);

    const [assignOpen, setAssignOpen] = useState(false);

    const level = useClientLevel();

    useEffect(() => {
        if (isOpen && level?.id) {
            fetchGroups();
            setSelectedIds([]);
        }
    }, [isOpen, level?.id]);

    const fetchGroups = async () => {
        setLoading(true);
        try {
            const query = JSON.stringify({ client_id: Number(level?.id) });
            const response = await baseapi.get(`/switch_groups/?query=${query}`);
            if (response.data.status) {
                setGroups(response.data.data || []);
            } else {
                showError('Failed to fetch switch groups');
            }
        } catch (error) {
            console.error('Error fetching switch groups:', error);
            showError('Error fetching switch groups');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const handleSelectOne = (id: number) => {
        if (selectedIds.includes(id)) {
            setSelectedIds([]);
        } else {
            setSelectedIds([id]);
        }
    };

    const handleAdd = () => {
        setAddEditMode('add');
        setSelectedGroup(null);
        setAddEditOpen(true);
    };

    const handleEdit = () => {
        if (selectedIds.length !== 1) return;
        const groupToEdit = groups.find(g => g.id === selectedIds[0]);
        if (groupToEdit) {
            setAddEditMode('edit');
            setSelectedGroup(groupToEdit);
            setAddEditOpen(true);
        }
    };

    const handleDelete = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} switch group(s)?`)) return;

        setDeleting(true);
        try {
            // Delete selected groups in parallel or sequentially.
            // Since they are multiple, we can run them in Promise.all
            const deletePromises = selectedIds.map(id => baseapi.delete(`/switch_groups/${id}/`));
            await Promise.all(deletePromises);

            showSuccess('Switch group(s) deleted successfully');
            setSelectedIds([]);
            fetchGroups();
        } catch (error) {
            console.error('Error deleting switch groups:', error);
            showError('Failed to delete some switch groups');
        } finally {
            setDeleting(false);
        }
    };

    const handleAssign = () => {
        if (selectedIds.length !== 1) return;
        const targetGroup = groups.find(g => g.id === selectedIds[0]);
        if (targetGroup) {
            setSelectedGroup(targetGroup);
            setAssignOpen(true);
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9990]" onClick={onClose} />
            <div className="fixed inset-0 flex items-center justify-center z-[9991] p-4">
                <div className="w-full max-w-4xl bg-gray-900 border border-gray-800 shadow-2xl rounded-xl text-white relative overflow-hidden flex flex-col max-h-[85vh]">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />

                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
                        <div className="flex items-center gap-2">
                            <Network className="w-5 h-5 text-orange-400" />
                            <h2 className="text-xl font-bold text-white">Switch Groups</h2>
                        </div>
                        <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-gray-800">
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 flex flex-col md:flex-row gap-6 overflow-y-auto flex-1">

                        {/* Left Column: Table */}
                        <div className="flex-1 min-h-[300px] border border-gray-800 rounded-lg bg-gray-950/30 overflow-hidden flex flex-col">
                            {loading ? (
                                <div className="flex-1 flex flex-col items-center justify-center gap-2 py-12">
                                    <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                                    <span className="text-xs text-gray-500 font-medium">Loading groups...</span>
                                </div>
                            ) : groups.length === 0 ? (
                                <div className="flex-1 flex flex-col items-center justify-center text-center p-8 opacity-60">
                                    <Network className="w-12 h-12 text-gray-700 mb-2" />
                                    <p className="text-sm font-semibold text-gray-400">No Switch Groups Found</p>
                                    <p className="text-xs text-gray-600 mt-1">Create one to organize and manage your switches.</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto flex-1">
                                    <table className="w-full text-left text-xs border-collapse">
                                        <thead className="bg-gray-900/80 text-gray-400 font-bold uppercase tracking-wider sticky top-0 border-b border-gray-800">
                                            <tr>
                                                <th className="px-4 py-3 w-10 text-center"></th>
                                                <th className="px-4 py-3 w-1/3">Name</th>
                                                <th className="px-4 py-3">Description</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-800/50">
                                            {groups.map((group) => {
                                                const isSelected = selectedIds.includes(group.id);
                                                return (
                                                    <tr
                                                        key={group.id}
                                                        onClick={() => handleSelectOne(group.id)}
                                                        className={`hover:bg-gray-800/40 cursor-pointer transition-colors ${isSelected ? 'bg-orange-500/5' : ''}`}
                                                    >
                                                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleSelectOne(group.id)}
                                                                className="rounded border-gray-750 bg-gray-800 text-orange-500 focus:ring-0 focus:ring-offset-0 cursor-pointer"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 font-semibold text-gray-200">
                                                            {group.name}
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-400 max-w-xs truncate">
                                                            {group.description || <span className="italic text-gray-600">No description</span>}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Right Column: Actions Button Panel */}
                        <div className="w-full md:w-36 flex flex-row md:flex-col gap-2 flex-shrink-0">
                            <button
                                onClick={handleAdd}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded-lg transition-colors"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Add
                            </button>

                            <button
                                onClick={handleEdit}
                                disabled={selectedIds.length !== 1}
                                className="flex-1 md:flex-none flex items-center justify-center gap-2 py-2 px-3 bg-gray-800 hover:bg-gray-700 disabled:opacity-40 disabled:hover:bg-gray-800 disabled:text-gray-500 text-gray-300 hover:text-white text-xs font-bold rounded-lg border border-gray-750 hover:border-gray-600 transition-all shadow-md"
                            >
                                <Edit2 className="w-3.5 h-3.5" />
                                Edit
                            </button>

                            <button
                                onClick={handleAssign}
                                disabled={selectedIds.length !== 1}
                                className="flex-1 md:flex-none flex items-center justify-center gap-2 py-2 px-3 bg-orange-600 hover:bg-orange-500 disabled:opacity-40 disabled:hover:bg-orange-600 disabled:text-gray-200 text-white text-xs font-bold rounded-lg transition-all shadow-md"
                            >
                                <Link2 className="w-3.5 h-3.5" />
                                Assign
                            </button>

                            <button
                                onClick={handleDelete}
                                disabled={selectedIds.length === 0 || deleting}
                                className="flex-1 md:flex-none flex items-center justify-center gap-2 py-2 px-3 bg-red-950/40 hover:bg-red-900/20 disabled:opacity-40 disabled:hover:bg-transparent disabled:text-gray-600 text-red-400 hover:text-red-300 text-xs font-bold rounded-lg border border-red-900/30 hover:border-red-900/50 transition-all shadow-md mt-0 md:mt-auto"
                            >
                                {deleting ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                    <Trash2 className="w-3.5 h-3.5" />
                                )}
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Nested Modal: Add / Edit Switch Group */}
            <AddEditSwitchGroupModal
                open={addEditOpen}
                onClose={() => setAddEditOpen(false)}
                onSuccess={fetchGroups}
                mode={addEditMode}
                group={selectedGroup}
            />

            {/* Nested Modal: Assign Switches */}
            {selectedGroup && (
                <AssignSwitchModal
                    open={assignOpen}
                    onClose={() => setAssignOpen(false)}
                    onSuccess={fetchGroups}
                    groupId={selectedGroup.id}
                    groupName={selectedGroup.name}
                />
            )}
        </>
    );
}
